import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBookings } from '../../context/BookingsContext';
import { useProperties } from '../../context/PropertiesContext';
import { 
  LayoutDashboard, 
  Users, 
  Building, 
  Calendar, 
  Star, 
  Bell, 
  
  FileText, 
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Scale,
  MessageCircle,
  Route,
  LogIn,
  Mail,
  AtSign,
  Video,
  ClipboardList
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { useState, useEffect } from 'react';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/properties', label: 'Properties', icon: Building },
  { path: '/admin/bookings', label: 'Bookings', icon: Calendar },
  { path: '/admin/whatsapp-inbox', label: 'WhatsApp Inbox', icon: MessageCircle },
  { path: '/admin/whatsapp-queue', label: 'WhatsApp Queue', icon: MessageCircle },
  { path: '/admin/guest-journeys', label: 'Guest Journeys', icon: Route },
  { path: '/admin/login-records', label: 'Login Records', icon: LogIn },
  { path: '/admin/bfr-email', label: 'BFR Email', icon: Mail },
  { path: '/admin/gmail-integration', label: 'Gmail Integration', icon: AtSign },
  { path: '/admin/tutorials', label: 'Tutorial Videos', icon: Video },
  { path: '/admin/walkin-builder', label: 'Walk-in Builder', icon: ClipboardList },
  { path: '/admin/reviews', label: 'Reviews', icon: Star },
  { path: '/admin/notifications', label: 'Notifications', icon: Bell },
  { path: '/admin/audit-logs', label: 'Audit Logs', icon: FileText },
  { path: '/admin/legal', label: 'Legal Settings', icon: Scale },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

const AdminLayout = () => {
  const { admin, logout } = useAuth();
  const { syncStatus, syncStats, lastFetched: bookingsLastFetched } = useBookings();
  const { lastFetched: propertiesLastFetched } = useProperties();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [timeLabel, setTimeLabel] = useState('');

  const lastUpdated = [propertiesLastFetched, bookingsLastFetched].filter(Boolean).sort((a, b) => b - a)[0];

  useEffect(() => {
    if (!lastUpdated) return;
    const tick = () => {
      const sec = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);
      if (sec < 5) setTimeLabel('Just now');
      else if (sec < 60) setTimeLabel(`${sec}s ago`);
      else if (sec < 3600) setTimeLabel(`${Math.floor(sec / 60)}m ago`);
      else setTimeLabel(`${Math.floor(sec / 3600)}h ago`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lastUpdated]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
          className="bg-white"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-screen w-64 bg-white border-r border-slate-200 flex flex-col z-40 transform transition-transform duration-200 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Building className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-slate-900 text-lg">Superhost</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-3 py-2">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-blue-100 text-blue-600 font-medium">
                {admin?.name?.charAt(0) || 'A'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{admin?.name || 'Admin'}</p>
              <p className="text-xs text-slate-500 truncate">{admin?.email || 'admin@superhost.com'}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-20" data-testid="admin-header">
          <div className="lg:hidden w-10" />
          <div className="hidden lg:flex items-center gap-3">
            <h1 className="text-lg font-semibold text-slate-900">Admin Panel</h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Live Sync Indicator */}
            {syncStatus === 'syncing' ? (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50/80 border border-blue-100 transition-all" data-testid="live-sync-indicator">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                <span className="text-xs font-medium text-blue-700">Syncing{syncStats?.progress ? ` ${syncStats.progress.current}/${syncStats.progress.total}` : ''}</span>
              </div>
            ) : syncStatus === 'error' ? (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50/80 border border-amber-100 transition-all" data-testid="live-sync-indicator">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                <span className="text-xs font-medium text-amber-700">Sync Issue</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50/80 border border-emerald-100 transition-all" data-testid="live-sync-indicator">
                <span className="live-dot" />
                <span className="text-xs font-medium text-emerald-700">Live</span>
                {timeLabel && (
                  <>
                    <span className="text-emerald-400 text-xs">&middot;</span>
                    <span className="text-xs text-emerald-600/70">{timeLabel}</span>
                  </>
                )}
              </div>
            )}

            {/* Notifications */}
            <Button variant="ghost" size="icon" className="relative" data-testid="header-notifications-btn">
              <Bell className="h-5 w-5 text-slate-600" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </Button>

            {/* User dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-dropdown-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-blue-100 text-blue-600 font-medium text-sm">
                      {admin?.name?.charAt(0) || 'A'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline text-sm font-medium text-slate-700">{admin?.name || 'Admin'}</span>
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/admin/settings')} data-testid="dropdown-profile">
                  <Settings className="mr-2 h-4 w-4" />
                  Profile Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="dropdown-logout">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
